import { ProjectStockSource } from '@/api/queries/project/useGetProjectStockSources';
import { Combobox, Field, Input, SelectOption } from '@payaca/ambrosia';
import { FC, useMemo, useState } from 'react';

type AvailableSupplier = { id: number; name: string };

export type OrderSourceSelection =
  | { kind: 'supplier'; supplierId: number }
  | { kind: 'stockLocation'; stockSource: ProjectStockSource };

type OptionMetadata = OrderSourceSelection;

const SUPPLIER_PREFIX = 'supplier:';
const STOCK_LOCATION_PREFIX = 'stockLocation:';

// The single source of truth for the Combobox option-value encoding, shared
// with callers that derive the currently-selected value, so the two sides stay
// in sync.
export const encodeSupplierSourceValue = (supplierId: number): string =>
  `${SUPPLIER_PREFIX}${supplierId}`;

export const encodeStockLocationSourceValue = (
  materialsListPublicId: string
): string => `${STOCK_LOCATION_PREFIX}${materialsListPublicId}`;

interface Props {
  /** Suppliers that can fulfil the project's outstanding materials. */
  suppliers: AvailableSupplier[];
  /** Stock locations that can supply the project's outstanding materials. */
  stockSources: ProjectStockSource[];
  /** Encoded value of the current selection, or undefined when nothing is picked. */
  selectedValue?: string;
  /** False once the wizard has advanced past source selection. */
  canChangeSource?: boolean;
  onChange: (selection: OrderSourceSelection | null) => void;
}

/**
 * The Create-Order wizard's source picker. Unlike the shared
 * SupplierSelectionControl, it offers both suppliers and stock locations in one
 * Combobox so the buyer can choose to source from stock rather than re-order.
 * Stock locations only appear when the backend returns them (gated by the stock
 * feature flag and permissions), so an account without stock sees suppliers
 * exactly as before.
 */
const OrderSourceSelectionField: FC<Props> = (props) => {
  const {
    suppliers,
    stockSources,
    selectedValue,
    canChangeSource = true,
    onChange,
  } = props;

  const [searchTerm, setSearchTerm] = useState<string>();

  const options = useMemo<SelectOption<string, OptionMetadata>[]>(() => {
    const supplierOptions = suppliers.map((supplier) => ({
      value: encodeSupplierSourceValue(supplier.id),
      label: supplier.name,
      groupId: 'suppliers',
      metadata: { kind: 'supplier' as const, supplierId: supplier.id },
    }));

    const stockLocationOptions = stockSources.map((stockSource) => ({
      value: encodeStockLocationSourceValue(stockSource.materialsList.publicId),
      label: stockSource.name,
      groupId: 'stockLocations',
      metadata: { kind: 'stockLocation' as const, stockSource },
    }));

    return [...supplierOptions, ...stockLocationOptions];
  }, [suppliers, stockSources]);

  const optionGroups = useMemo(
    () => [
      { id: 'suppliers', label: 'Suppliers' },
      ...(stockSources.length
        ? [{ id: 'stockLocations', label: 'Stock locations' }]
        : []),
    ],
    [stockSources.length]
  );

  const selectedLabel = options.find(
    (option) => option.value === selectedValue
  )?.label;

  return (
    <Field.Legacy className="mb-2.5" name="orderSource">
      <Field.Label>Order from</Field.Label>
      {canChangeSource ? (
        <Combobox
          value={selectedValue}
          options={options}
          optionGroups={optionGroups}
          query={searchTerm}
          setQuery={setSearchTerm}
          placeholder="Start typing to search for a supplier or stock location..."
          inputPlaceholder="Start typing to search for a supplier or stock location..."
          onOptionClick={(_event, option) => {
            onChange(option?.metadata ?? null);
            setSearchTerm(undefined);
          }}
        />
      ) : (
        <Input value={selectedLabel ?? ''} disabled />
      )}
    </Field.Legacy>
  );
};

export default OrderSourceSelectionField;
